import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle } from 'lucide-react';
import { consumeStoredRedirect } from '../utils/redirect';
import { supabase } from '../services/supabase';
import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Button } from '../components/common/Button';


export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Consume pending redirect from localStorage (set by LoginPage)
        const redirectTo = consumeStoredRedirect() ?? '/';

        // Get the session from the URL hash
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (data.session) {
          setStatus('success');
          // Wait a bit for the animation, then redirect
          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 1500);
        } else {
          // No session found, try to exchange the token from URL
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');

          if (!accessToken) {
            throw new Error('Nenhum token encontrado');
          }

          setStatus('success');
          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 1500);
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setErrorMessage(
          error instanceof Error ? error.message : 'Erro ao processar autenticação'
        );
        setStatus('error');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <PageContainer className="flex items-center justify-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <Logo size="lg" className="mb-8" />

        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-white/60">Verificando seu acesso...</p>
          </div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-20 h-20 rounded-full bg-success-green/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-success-green" />
            </div>
            <h2 className="text-xl font-semibold text-white">Acesso confirmado!</h2>
            <p className="text-white/60">Redirecionando...</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-20 h-20 rounded-full bg-coral/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-coral" />
            </div>
            <h2 className="text-xl font-semibold text-white">Algo deu errado</h2>
            <p className="text-white/60 max-w-xs">{errorMessage}</p>
            <Button onClick={() => navigate('/login')} variant="secondary">
              Voltar para o login
            </Button>
          </motion.div>
        )}
      </motion.div>
    </PageContainer>
  );
}

